import { useEffect, useState, useMemo } from "react";
import { useTranslation } from 'react-i18next';
import { transformTranslatableFields } from "../api/axios";

// Stable reference for default fields to prevent infinite loops
const DEFAULT_FIELDS = ['name', 'title', 'description'];

/**
 * Keeps translated data in sync with language changes.
 * @param {any} rawData - The raw API data (with all language fields).
 * @param {string[]} fields - Optional array of field names to translate specifically.
 * @returns {any} The translated data for the current language.
 */
export function useTranslatableData(rawData, fields = DEFAULT_FIELDS) {
  // Use the i18n object from useTranslation for more reliable language detection
  const { i18n } = useTranslation();
  
  // Memoize fields to prevent unnecessary re-renders when array contents are the same
  const memoizedFields = useMemo(() => fields, [JSON.stringify(fields.sort())]);
  
  // Always compute translated data based on current language - don't use lazy initialization
  const [translated, setTranslated] = useState(transformTranslatableFields(rawData, memoizedFields));

  // Re-translate whenever rawData, fields, or language changes
  useEffect(() => {
    const newTranslated = transformTranslatableFields(rawData, memoizedFields);
    setTranslated(newTranslated);
  }, [rawData, memoizedFields, i18n.language]);

  return translated;
}